import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { IMAGES, SITE } from "../../data/content";
import { Reveal } from "./Reveal";

const TYPES = ["1-to-1 individual lessons", "Group lessons"];
const HOURS = ["6 hours (£30/hr)", "12 hours (2 hours free)", "30 hours (£27/hr)"];
const DIALECTS = [
  "Levantine Arabic (preferred)",
  "Modern Standard Arabic",
  "Gulf Arabic",
  "Egyptian dialect",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    type: TYPES[0],
    hours: HOURS[1],
    dialect: DIALECTS[0],
    message: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = `Lesson enquiry — ${form.name || "new student"}`;
    const body = [
      "Hello London Empire Institution,",
      "",
      "I would like to arrange Arabic lessons.",
      "",
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      `Lesson type: ${form.type}`,
      `Package: ${form.hours}`,
      `Dialect: ${form.dialect}`,
      "",
      form.message,
      "",
    ]
      .filter((line) => line !== false)
      .join("\n");
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email app…");
  };

  return (
    <section id="contact" className="border-t border-line bg-sand/50 py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="eyebrow">Get in touch</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-ink sm:text-4xl md:text-5xl">
              Request your lessons
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-soft">
              Send us a message and we&apos;ll agree your package — 6, 12 or 30 hours — then
              schedule lessons around you.
            </p>
          </Reveal>

          <div className="mt-10 space-y-4">
            <Reveal delay={0.05}>
              <a
                data-testid="contact-call-button"
                href={`tel:${SITE.phoneTel}`}
                className="group flex items-center gap-5 rounded-2xl border border-line bg-surface p-5 transition-colors duration-300 hover:border-pine"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-pine text-paper">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-ink-mute">
                    Call us
                  </span>
                  <span className="block font-semibold text-ink group-hover:text-pine">
                    {SITE.phoneDisplay}
                  </span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                data-testid="contact-whatsapp-button"
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-5 rounded-2xl border border-line bg-surface p-5 transition-colors duration-300 hover:border-jade"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-jade text-paper">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-ink-mute">
                    WhatsApp
                  </span>
                  <span className="block font-semibold text-ink group-hover:text-pine">
                    Message us on WhatsApp
                  </span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.15}>
              <a
                data-testid="contact-email-link"
                href={`mailto:${SITE.email}`}
                className="group flex items-center gap-5 rounded-2xl border border-line bg-surface p-5 transition-colors duration-300 hover:border-gold"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold text-paper">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-ink-mute">
                    Email
                  </span>
                  <span className="block break-all font-semibold text-ink group-hover:text-pine">
                    {SITE.email}
                  </span>
                </span>
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-8 overflow-hidden rounded-3xl border border-line">
              <img
                src={IMAGES.wembley}
                alt="Wembley Park, London"
                className="h-56 w-full object-cover"
              />
            </div>
            <p
              data-testid="location-address"
              className="mt-4 flex items-center gap-2 text-sm text-ink-soft"
            >
              <MapPin className="h-4 w-4 text-jade" />
              {SITE.addressFull}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} y={36}>
          <form
            onSubmit={onSubmit}
            className="rounded-3xl border border-line bg-surface p-8 shadow-[0_24px_48px_-24px_rgba(10,35,28,0.18)] md:p-10"
          >
            <h3 className="font-serif text-2xl text-ink">Enquiry form</h3>
            <p className="mt-2 text-sm text-ink-mute">
              Opens your email app with everything pre-filled.
            </p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">Name</label>
                <input
                  data-testid="enquiry-form-name-input"
                  className="field"
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">Email</label>
                <input
                  data-testid="enquiry-form-email-input"
                  type="email"
                  className="field"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">
                  Phone (optional)
                </label>
                <input
                  data-testid="enquiry-form-phone-input"
                  type="tel"
                  className="field"
                  placeholder="Your phone number"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">
                  Lesson type
                </label>
                <select
                  data-testid="enquiry-form-type-select"
                  className="field"
                  value={form.type}
                  onChange={set("type")}
                >
                  {TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">Hours</label>
                <select
                  data-testid="enquiry-form-hours-select"
                  className="field"
                  value={form.hours}
                  onChange={set("hours")}
                >
                  {HOURS.map((h) => (
                    <option key={h}>{h}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">
                  Dialect
                </label>
                <select
                  data-testid="enquiry-form-dialect-select"
                  className="field"
                  value={form.dialect}
                  onChange={set("dialect")}
                >
                  {DIALECTS.map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-ink-soft">
                  Message (optional)
                </label>
                <textarea
                  data-testid="enquiry-form-message-input"
                  className="field min-h-[110px] resize-y"
                  placeholder="Your level, goals, availability…"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>
            </div>

            <button
              data-testid="enquiry-form-submit-button"
              type="submit"
              className="btn-primary mt-8 w-full justify-center"
            >
              <Send className="h-4 w-4" />
              Send enquiry by email
            </button>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-ink-mute">
              By sending, we&apos;ll agree your paid hours together. Cancellation requires 48
              hours&apos; notice.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
